import { Sidebar as UiSidebar, SidebarContent, SidebarHeader } from "@/components/ui/sidebar";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Home, PieChart, CalendarDays, Receipt, Calculator, HelpCircle, Settings as SettingsIcon, LogOut } from "lucide-react";
import { motion } from "framer-motion";
import { useTheme } from "@/App";
import { supabase } from "@/lib/supabase";

const LOGO_URL =
  "https://res.cloudinary.com/dlmbskxuc/image/upload/v1745797729/logo1h_pmrhki.png";

export function AppSidebar() {
  const LOGO_X = 0; // px: + right, - left
  const LOGO_Y = -18; // px: + down, - up
  const location = useLocation();
  const { isDark } = useTheme();

  const navItems = [
    { icon: Home, path: "/" },
    { icon: PieChart, path: "/stats" },
    { icon: CalendarDays, path: "/calendar" },
    { icon: Receipt, path: "/invoice" },
    { icon: Calculator, path: "/taxes" },
    { icon: HelpCircle, path: "/help" },
  ];

  return (
    <UiSidebar 
      side="left" 
      variant="sidebar" 
      collapsible="none"
      className="border-none bg-transparent p-4 md:p-8 w-16 md:w-32"
    >
      <SidebarHeader className="p-0 flex justify-center">
        <div className="w-full flex justify-center">
          <img
            src="https://res.cloudinary.com/dlmbskxuc/image/upload/c_crop,w_175,h_170/v1745797729/logo1h_pmrhki.png"
            alt="Firsthold"
            draggable={false}
            className="object-contain mt-[20px] mb-[20px] pt-[0px] pb-[0px]"
            style={{
              width: "clamp(60px, 8vw, 110px)",
              height: "clamp(60px, 8vw, 110px)",
              transform: `translate(${LOGO_X}px, ${LOGO_Y}px)`,
            }}
          />
        </div>
      </SidebarHeader>
      <SidebarContent className="mt-13 bg-white/40 backdrop-blur-2xl rounded-[2.5rem] border border-white/60 shadow-xl shadow-blue-900/5 flex flex-col items-center py-10 gap-8 overflow-visible h-auto min-h-0 shrink-0 [@supports_not_(backdrop-filter:blur(1px))]:bg-white/95">
        <div className="flex flex-col items-center gap-8 w-full">
          {navItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            return (
              <Link key={index} to={item.path}>
                <motion.div 
                  whileHover={{ scale: 1.1, x: 5 }}
                  whileTap={{ scale: 0.95 }}
                  className={cn(
                    "w-10 h-10 rounded-xl flex items-center justify-center transition-all cursor-pointer relative",
                    isActive 
                      ? "bg-white text-black shadow-lg shadow-black/5 border border-gray-100" 
                      : "text-gray-400 hover:text-black"
                  )}
                >
                  <item.icon size={20} />
                  {isActive && (
                    <motion.div 
                      layoutId="active-nav"
                      className="absolute -right-3 w-1.5 h-1.5 rounded-full bg-black" 
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </motion.div>
              </Link>
            );
          })}
        </div>
        <div className="mt-12 flex flex-col items-center gap-6 w-full">
          <Link to="/settings">
            <motion.div
              whileHover={{ scale: 1.1, rotate: 15 }}
              whileTap={{ scale: 0.95 }}
              className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center transition-all cursor-pointer",
                location.pathname === "/settings" 
                  ? "bg-white text-black shadow-lg shadow-black/5 border border-gray-100" 
                  : "text-gray-400 hover:text-black"
              )}
            >
              <SettingsIcon size={20} />
            </motion.div>
          </Link>
          <motion.button
            whileHover={{ scale: 1.1, x: 5 }}
            whileTap={{ scale: 0.95 }}
            onClick={async () => {
              // Clear onboarding cache FIRST so AuthProvider's optimistic path doesn't
              // immediately re-hydrate the session and loop the user back to the dashboard.
              try { localStorage.removeItem('fh_onboarding_complete'); } catch { /* ignore */ }
              try { await supabase.auth.signOut(); } catch { /* ignore */ }
              window.location.href = '/login';
            }}
            className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
            title="Log out"
          >
            <LogOut size={20} />
          </motion.button>
        </div>
      </SidebarContent>
    </UiSidebar>
  );
}
